/**
 * QZ Print Context
 * 
 * Provides QZ Tray print functionality throughout the app.
 * Handles connection management, printer configuration, and status updates.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import qzPrintService, { QZStatus, PrinterStatus } from '../services/QZPrintService';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const QZPrintContext = createContext(null);

export function QZPrintProvider({ children }) {
  const { api, user } = useAuth();
  const [status, setStatus] = useState(qzPrintService.getStatus());
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  // Initialize QZ Tray connection
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const initQZ = async () => {
      setLoading(true);
      try {
        await qzPrintService.initialize();
      } catch (err) {
        console.error('[QZContext] Init failed:', err);
      } finally {
        setLoading(false);
      }
    };

    initQZ();

    // Add listener for status changes
    const unsubscribe = qzPrintService.addListener((newStatus) => {
      setStatus({ ...newStatus });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Load printer config when user/outlet changes
  useEffect(() => {
    if (!user?.outlet_id) return;

    const loadConfig = async () => {
      try {
        // Try to load from backend settings
        const response = await api.get('/printers/qz-config');
        if (response.data) {
          qzPrintService.loadPrinterConfig(response.data);
          setStatus(qzPrintService.getStatus());
        }
      } catch (err) {
        // If no backend config, try localStorage
        const localConfig = localStorage.getItem(`qz_printer_config_${user.outlet_id}`);
        if (localConfig) {
          try {
            qzPrintService.loadPrinterConfig(JSON.parse(localConfig));
            setStatus(qzPrintService.getStatus());
          } catch (e) {
            console.warn('Failed to parse local printer config');
          }
        }
      }
    };

    loadConfig();
  }, [user?.outlet_id, api]);

  // Auto-assign first printer as receipt printer if not configured
  useEffect(() => {
    const currentStatus = qzPrintService.getStatus();
    const printers = currentStatus.printers || [];
    const config = currentStatus.config || {};
    
    // If connected, has printers, but no receipt printer configured
    if (currentStatus.isConnected && printers.length > 0 && !config.receipt) {
      const firstPrinter = printers[0]?.name;
      if (firstPrinter) {
        console.log('[QZContext] Auto-assigning first printer as receipt:', firstPrinter);
        qzPrintService.setPrinterConfig('receipt', firstPrinter);
        
        // Save to localStorage
        if (user?.outlet_id) {
          const newConfig = { ...config, receipt: firstPrinter };
          localStorage.setItem(`qz_printer_config_${user.outlet_id}`, JSON.stringify(newConfig));
          
          // Try to save to backend
          api.put('/printers/qz-config', newConfig).catch(err => {
            console.warn('Failed to save auto-assigned printer config to backend');
          });
        }
        
        setStatus(qzPrintService.getStatus());
        toast.info(`Printer "${firstPrinter}" otomatis dipilih sebagai Receipt Printer`);
      }
    }
  }, [status.isConnected, status.printers?.length, status.config?.receipt, user?.outlet_id, api]);

  // Connect to QZ Tray
  const connect = useCallback(async () => {
    setLoading(true);
    try {
      const success = await qzPrintService.connect();
      if (success) {
        toast.success('Terhubung ke QZ Tray');
      } else {
        toast.error('Gagal terhubung ke QZ Tray');
      }
      return success;
    } finally {
      setLoading(false);
    }
  }, []);

  // Disconnect from QZ Tray
  const disconnect = useCallback(async () => {
    await qzPrintService.disconnect();
    toast.info('Terputus dari QZ Tray');
  }, []);

  // Refresh printer list and auto-assign receipt printer if needed
  const refreshPrinters = useCallback(async () => {
    if (!status.isConnected) {
      toast.error('QZ Tray tidak terhubung');
      return [];
    }
    
    await qzPrintService.refreshPrinters();
    const newStatus = qzPrintService.getStatus();
    
    // Auto-assign first printer if no receipt printer configured
    if (newStatus.printers?.length > 0 && !newStatus.config?.receipt) {
      const firstPrinter = newStatus.printers[0]?.name;
      if (firstPrinter) {
        console.log('[QZContext] Auto-assigning first printer after scan:', firstPrinter);
        qzPrintService.setPrinterConfig('receipt', firstPrinter);
        
        // Save config
        if (user?.outlet_id) {
          const newConfig = { ...newStatus.config, receipt: firstPrinter };
          localStorage.setItem(`qz_printer_config_${user.outlet_id}`, JSON.stringify(newConfig));
          api.put('/printers/qz-config', newConfig).catch(() => {});
        }
        
        toast.info(`Printer "${firstPrinter}" otomatis dipilih sebagai Receipt Printer`);
      }
    }
    
    setStatus(qzPrintService.getStatus());
    return newStatus.printers;
  }, [status.isConnected, user?.outlet_id, api]);

  // Get printer status
  const getPrinterStatus = useCallback(async (printerName) => {
    return qzPrintService.getPrinterStatus(printerName);
  }, []);

  // Configure printer for role
  const configurePrinter = useCallback(async (role, printerName) => {
    qzPrintService.setPrinterConfig(role, printerName);
    
    // Save to localStorage
    if (user?.outlet_id) {
      const config = qzPrintService.getStatus().config;
      localStorage.setItem(`qz_printer_config_${user.outlet_id}`, JSON.stringify(config));
      
      // Also try to save to backend
      try {
        await api.put('/printers/qz-config', config);
      } catch (err) {
        // Silent fail for backend save - localStorage is primary
        console.warn('Failed to save printer config to backend');
      }
    }
    
    setStatus(qzPrintService.getStatus());
  }, [user?.outlet_id, api]);

  // Test print
  const testPrint = useCallback(async (printerName) => {
    try {
      await qzPrintService.testPrint(printerName);
      toast.success(`Test print berhasil: ${printerName}`);
      return { success: true };
    } catch (err) {
      toast.error(`Test print gagal: ${err.message}`);
      return { success: false, error: err.message };
    }
  }, []);

  // Print receipt (NO FALLBACK)
  const printReceipt = useCallback(async (order, outlet, options = {}) => {
    if (!status.isConnected) {
      throw new Error('QZ Tray tidak terhubung. Receipt harus dicetak via QZ Tray.');
    }
    
    if (!status.config.receipt) {
      throw new Error('Printer kasir belum dikonfigurasi.');
    }

    try {
      const result = await qzPrintService.printReceipt(order, outlet, options);
      
      // Update print job status in backend
      if (order.id) {
        try {
          await api.patch(`/print-jobs/order/${order.id}/status`, { 
            status: 'PRINTED',
            method: 'QZ_TRAY'
          });
        } catch (e) {
          console.warn('Failed to update print job status');
        }
      }
      
      return result;
    } catch (err) {
      // Log failed print
      if (order.id) {
        try {
          await api.patch(`/print-jobs/order/${order.id}/status`, { 
            status: 'FAILED',
            error: err.message,
            method: 'QZ_TRAY'
          });
        } catch (e) {
          console.warn('Failed to update print job status');
        }
      }
      
      throw err; // Re-throw - receipt has NO fallback
    }
  }, [status.isConnected, status.config.receipt, api]);

  // Print kitchen ticket (with fallback option)
  const printKitchen = useCallback(async (order, options = {}) => {
    const result = await qzPrintService.printKitchen(order, options);
    
    // Update backend
    if (order.id && result.success) {
      try {
        await api.patch(`/print-jobs/order/${order.id}/kitchen/status`, { 
          status: 'PRINTED',
          method: 'QZ_TRAY'
        });
      } catch (e) {
        console.warn('Failed to update kitchen print job status');
      }
    }
    
    return result;
  }, [api]);

  // Print bar ticket
  const printBar = useCallback(async (order, options = {}) => {
    const result = await qzPrintService.printBar(order, options);
    
    if (order.id && result.success) {
      try {
        await api.patch(`/print-jobs/order/${order.id}/bar/status`, { 
          status: 'PRINTED',
          method: 'QZ_TRAY'
        });
      } catch (e) {
        console.warn('Failed to update bar print job status');
      }
    }
    
    return result;
  }, [api]);

  // Print bill
  const printBill = useCallback(async (order, outlet, options = {}) => {
    return qzPrintService.printBill(order, outlet, options);
  }, []);

  // Print raw ESC/POS
  const printRaw = useCallback(async (printerName, data, options = {}) => {
    return qzPrintService.printRaw(printerName, data, options);
  }, []);

  // Open cash drawer
  const openDrawer = useCallback(async (printerName) => {
    try {
      await qzPrintService.openDrawer(printerName);
      toast.success('Laci kas dibuka');
      return { success: true };
    } catch (err) {
      toast.error(`Gagal membuka laci kas: ${err.message}`);
      return { success: false, error: err.message };
    }
  }, []);

  const value = {
    // Status
    status: status.connection,
    isConnected: status.isConnected,
    isActive: status.isActive,
    error: status.error,
    loading,
    
    // Printers
    printers: status.printers,
    printerConfig: status.config,
    
    // Connection
    connect,
    disconnect,
    
    // Printer management
    refreshPrinters,
    getPrinterStatus,
    configurePrinter,
    testPrint,
    
    // Printing
    printReceipt,
    printKitchen,
    printBar,
    printBill,
    printRaw,
    openDrawer,
    
    // Helpers
    canPrintReceipt: status.isConnected && !!status.config.receipt,
    canPrintKitchen: status.isConnected && !!status.config.kitchen,
    canPrintBar: status.isConnected && !!status.config.bar,
  };

  return (
    <QZPrintContext.Provider value={value}>
      {children}
    </QZPrintContext.Provider>
  );
}

export function useQZPrint() {
  const context = useContext(QZPrintContext);
  if (!context) {
    throw new Error('useQZPrint must be used within a QZPrintProvider');
  }
  return context;
}

export { QZStatus, PrinterStatus };
export default QZPrintContext;
